import {
	createContext,
	type PropsWithChildren,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useAnalytics } from 'use-analytics'

export type AxeptioEventName =
	| 'consent:saved'
	| 'cookies:complete'
	| 'cookies:step:change'
	| 'cookies:step.startTimeout'
	| 'cookies:step.stopTimeout'
	| 'token:update'
	| 'overlayOpenWidget'
	| 'overlayOpenCookies'
	| 'showProcessingDetails'
	| 'close'

type AxeptioProviders = '$$googleConsentMode'

export type Choices = Record<AxeptioProviders, { analytics_storage: string }>

export type Cb = (choices?: Choices) => void

interface AxeptioEventHandlers {
	on: (eventName: AxeptioEventName, cb: Cb) => void
}

type AxeptioArrayEl = (axeptio: AxeptioEventHandlers) => void

export interface AxeptioSDK {
	push: (axeptio: AxeptioEventHandlers) => void
}

// https://developers.axeptio.eu/v/francais/sdk/integration-du-sdk
declare global {
	interface Window {
		axeptioSettings?: {
			clientId?: string
			tokenInstance?: unknown
			cookiesVersion?: string
			triggerGTMEvents?: boolean
			jsonCookieName?: string
			authorizedVendorsCookieName?: string
			allVendorsCookieName?: string
			userCookiesDuration?: number
			userCookiesDomain?: string
			userCookiesSameSite?: string
			userCookiesSecure?: boolean
			openCookiesWidgetIfVendorsMismatch?: boolean
			mountClassName?: string
			apiUrl?: string
			googleConsentMode?: { default: { analytics_storage: string } }
		}
		openAxeptioCookies: VoidFunction
		_axcb?: AxeptioArrayEl[]
	}
}

interface AxeptioContextType {
	choices: Choices | undefined
	axeptioQueue?: AxeptioArrayEl[]
}

const AxeptioContext = createContext<AxeptioContextType | undefined>(undefined)

export const AxeptioProvider = ({
	children,
	clientId,
}: PropsWithChildren<{ clientId: string }>) => {
	const analytics = useAnalytics()
	const [choices, setChoices] = useState<Choices | undefined>()
	const axeptioHandlerAddedRef = useRef(false)

	// Add axeptio script to the document
	useEffect(() => {
		if (
			!clientId ||
			typeof window === 'undefined' ||
			document.getElementById('axeptio-script')
		) {
			return undefined
		}

		window.axeptioSettings = {
			clientId,
			cookiesVersion: 'cocoon-space',
			googleConsentMode: {
				default: {
					analytics_storage: 'granted',
				},
			},
			mountClassName: 'pointer-events-auto',
		}

		const script = document.createElement('script')
		script.id = 'axeptio-script'
		script.type = 'text/javascript'
		script.src = '//static.axept.io/sdk-slim.js'
		script.async = true

		document.body.appendChild(script)
	}, [clientId])

	// Get axeptio queue
	const axeptioQueue = useMemo(() => {
		if (typeof window === 'undefined') return
		window._axcb = window._axcb || []
		return window._axcb
	}, [])

	// Add axeptio event handler to the queue
	useEffect(() => {
		if (!axeptioHandlerAddedRef.current) {
			axeptioHandlerAddedRef.current = true

			axeptioQueue?.push((axeptio) => {
				axeptio.on('cookies:complete', (choices) => {
					// Set choices
					setChoices(choices)

					// Enable Google Tag Manager if analytics_storage is granted
					if (choices?.$$googleConsentMode.analytics_storage === 'granted') {
						analytics.plugins.enable('google-tag-manager')
						console.log('GTM enabled')
					} else {
						analytics.plugins.disable('google-tag-manager', () => {
							console.log('GTM disabled')
						})
					}
				})
			})
		}
	}, [axeptioQueue, analytics])

	const value = useMemo(
		() => ({ axeptioQueue, choices }),
		[choices, axeptioQueue],
	)

	return (
		<AxeptioContext.Provider value={value}>{children}</AxeptioContext.Provider>
	)
}

export const useAxeptio = () => {
	const context = useContext(AxeptioContext)
	if (context === undefined) {
		throw new Error('useAxeptioContext must be used within an AxeptioProvider')
	}
	return context
}
